import React, { useState } from 'react';
import { 
  LayoutDashboard,
  FileText,
  Wrench,
  Users,
  Calendar,
  ClipboardList,
  Upload,
  Image,
  Shield,
  Star,
  Bot,
  ArrowRight,
  Code,
  UserCog,
  Database,
  Settings,
  ChevronDown,
  ChevronRight,
  LogOut,
  Home,
  Menu,
  X,
  Car,
  HelpCircle,
  BookOpen,
  MessageSquare,
  Mail,
  Building2,
  BarChart3,
  Palette,
  Globe,
  Bell
} from 'lucide-react';
import { Button } from './ui/button';

const AdminSidebar = ({ activeTab, setActiveTab, onLogout, user }) => {
  const [expandedGroups, setExpandedGroups] = useState({
    content: true,
    bookings: true,
    media: false,
    feedback: false,
    technical: false,
    settings: false
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setIsMobileOpen(false);
  };

  const menuStructure = [
    {
      id: 'overview',
      label: 'Dashbord',
      icon: LayoutDashboard,
      single: true
    },
    {
      id: 'content',
      label: 'Innhold',
      icon: FileText,
      items: [
        { id: 'pages', label: 'Sider', icon: FileText },
        { id: 'cms', label: 'CMS Innhold', icon: Upload },
        { id: 'services', label: 'Tjenester', icon: Wrench },
        { id: 'cases', label: 'Referanser', icon: Users },
        { id: 'bulk', label: 'Bulk administrer', icon: ClipboardList }
      ]
    },
    {
      id: 'bookings',
      label: 'Bookinger',
      icon: Calendar,
      items: [
        { id: 'calendar', label: 'Kalender', icon: Calendar },
        { id: 'bookings', label: 'Oversikt', icon: ClipboardList },
        { id: 'bookings-bulk', label: 'Masseredigering', icon: ClipboardList },
        { id: 'customers', label: 'Kunder', icon: Users },
        { id: 'vehicles', label: 'Kjøretøy', icon: Car },
        { id: 'slots', label: 'Tidsluke-innstillinger', icon: Settings },
        { id: 'notification-settings', label: 'Varsel-innstillinger', icon: Bell }
      ]
    },
    {
      id: 'media',
      label: 'Media',
      icon: Image,
      items: [
        { id: 'media', label: 'Mediebibliotek', icon: Image },
        { id: 'partners', label: 'Partnere & Logoer', icon: Shield }
      ]
    },
    {
      id: 'feedback',
      label: 'Tilbakemelding',
      icon: Star,
      items: [
        { id: 'reviews', label: 'Anmeldelser', icon: Star },
        { id: 'feedback', label: 'Bruker-feedback', icon: MessageSquare },
        { id: 'chatbot', label: 'AI Chatbot', icon: Bot }
      ]
    },
    {
      id: 'marketing',
      label: 'Markedsføring',
      icon: Mail,
      items: [
        { id: 'email-campaigns', label: 'E-postkampanjer', icon: Mail }
      ]
    },
    {
      id: 'reports',
      label: 'Rapporter',
      icon: BarChart3,
      single: true,
      tabId: 'reports'
    },
    {
      id: 'technical',
      label: 'Teknisk',
      icon: Code,
      items: [
        { id: 'redirects', label: '301 Redirects', icon: ArrowRight },
        { id: 'scripts', label: 'Scripts & Kode', icon: Code }
      ],
      masterOnly: true
    },
    {
      id: 'users',
      label: 'Brukere',
      icon: UserCog,
      single: true,
      tabId: 'users',
      masterOnly: true
    },
    {
      id: 'settings',
      label: 'Innstillinger',
      icon: Settings,
      items: [
        { id: 'company-info', label: 'Bedriftsinfo', icon: Building2 },
        { id: 'theme-settings', label: 'Tema & Utseende', icon: Palette },
        { id: 'schema-test', label: 'Schema Test', icon: Globe },
        { id: 'backup', label: 'Backup', icon: Database }
      ],
      masterOnly: true
    }
  ];

  const isMasterAdmin = user?.role === 'master_admin';

  const renderMenuItem = (item, isSubItem = false) => {
    if (item.masterOnly && !isMasterAdmin) return null;

    const isActive = activeTab === (item.tabId || item.id);
    const Icon = item.icon;

    return (
      <button
        key={item.id}
        onClick={() => handleTabClick(item.tabId || item.id)}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
          ${isSubItem ? 'pl-10' : ''}
          ${isActive 
            ? 'bg-red-600 text-white' 
            : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }
        `}
      >
        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
        <span>{item.label}</span>
      </button>
    );
  };

  const renderMenuGroup = (group) => {
    if (group.masterOnly && !isMasterAdmin) return null;

    if (group.single) {
      return renderMenuItem(group);
    }

    const isExpanded = expandedGroups[group.id];
    const hasActiveChild = group.items?.some(item => activeTab === item.id);
    const Icon = group.icon;

    return (
      <div key={group.id} className="mb-1">
        <button
          onClick={() => toggleGroup(group.id)}
          className={`
            w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all
            ${hasActiveChild 
              ? 'text-red-400 bg-red-600/10' 
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }
          `}
        >
          <div className="flex items-center gap-3">
            <Icon className={`w-4 h-4 ${hasActiveChild ? 'text-red-400' : 'text-gray-500'}`} />
            <span>{group.label}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        
        {isExpanded && (
          <div className="mt-1 space-y-1">
            {group.items?.map(item => {
              if (item.masterOnly && !isMasterAdmin) return null;
              return renderMenuItem(item, true);
            })}
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <>
      {/* Logo/Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-yellow-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg">S</span>
          </div>
          <div>
            <h1 className="text-white font-bold">Sand-tec Admin</h1>
            <p className="text-xs text-gray-500">{user?.role === 'master_admin' ? 'Master Admin' : 'Admin'}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {menuStructure.map(group => renderMenuGroup(group))}
      </nav>

      {/* Footer Actions */}
      <div className="p-3 border-t border-gray-800 space-y-2">
        <button
          onClick={() => handleTabClick('help')}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'help' 
              ? 'bg-blue-600 text-white' 
              : 'text-blue-400 hover:text-blue-300 hover:bg-blue-600/10'
          }`}
        >
          <BookOpen className={`w-4 h-4 ${activeTab === 'help' ? 'text-white' : ''}`} />
          <span>Brukermanual</span>
        </button>
        <a
          href="/"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-all"
        >
          <Home className="w-4 h-4 text-gray-500" />
          <span>Til hjemmeside</span>
        </a>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-600/10 transition-all"
        >
          <LogOut className="w-4 h-4" />
          <span>Logg ut</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
      >
        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-gray-900 border-r border-gray-800 h-screen sticky top-0">
        {sidebarContent}
      </aside>

      {/* Sidebar - Mobile */}
      <aside 
        className={`
          lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-gray-900 border-r border-gray-800 z-50
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default AdminSidebar;
